import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const updated = 'March 31, 2026';

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 py-12 sm:px-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 mb-6">
          <ArrowLeft className="h-3 w-3" /> Back
        </button>

        <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="privacy-policy-title">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: {updated}</p>

        <div className="prose prose-slate prose-sm max-w-none space-y-6 text-slate-700 leading-relaxed">

          <section>
            <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-3">1. Introduction</h2>
            <p>
              Sharda LMS ("we," "our," or "us") operates the Sharda Lead Management System, accessible at shardalms.com (the "Service"). 
              This Privacy Policy explains what information we collect, how we use it, and your rights regarding your data.
            </p>
            <p>
              By using our Service, you agree to the collection and use of information as described in this Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-3">2. Information We Collect</h2>
            
            <h3 className="text-base font-medium text-slate-700 mt-4 mb-2">2.1 Information You Provide</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Account Information:</strong> Name, email address, and password when you create an account.</li>
              <li><strong>Content:</strong> Text, images, blog posts, social media content, and any other materials you upload or create through the Service.</li>
              <li><strong>Communications:</strong> Messages, comments, and feedback you send to us or through the Service.</li>
              <li><strong>Lead & Business Data:</strong> Customer and lead information you input into the system for management purposes.</li>
            </ul>

            <h3 className="text-base font-medium text-slate-700 mt-4 mb-2">2.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the Service, and click patterns.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, IP address, and device identifiers.</li>
              <li><strong>Session Data:</strong> Login times, session duration, and access logs for security purposes.</li>
            </ul>

            <h3 className="text-base font-medium text-slate-700 mt-4 mb-2">2.3 Information from Third-Party Services</h3>
            <p>When you connect third-party platforms (Facebook, Instagram, LinkedIn, WordPress, Canva), we may receive:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Facebook/Instagram:</strong> Your public profile information, Page details, post engagement metrics (likes, comments, shares, impressions), and content publishing capabilities as authorized by you.</li>
              <li><strong>LinkedIn:</strong> Your profile name, email, and post engagement data as authorized by you.</li>
              <li><strong>WordPress:</strong> Publishing access to your WordPress site as configured.</li>
              <li><strong>Canva:</strong> Design access and export capabilities as authorized by you.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-3">3. How We Use Your Information</h2>
            <p>We process your information for the following purposes:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Provide the Service:</strong> To operate, maintain, and improve the Sharda LMS platform, including lead management, content scheduling, and social media publishing.</li>
              <li><strong>Social Media Management:</strong> To publish content to connected platforms, retrieve engagement analytics, and display performance metrics on your behalf.</li>
              <li><strong>AI-Powered Features:</strong> To generate content suggestions, blog posts, image descriptions, and competitor analysis using AI services.</li>
              <li><strong>Security:</strong> To monitor for unauthorized access, detect suspicious activity, enforce rate limits, and protect user accounts.</li>
              <li><strong>Communication:</strong> To send you service-related notifications, updates, and respond to your inquiries.</li>
              <li><strong>Analytics:</strong> To understand how the Service is used and to improve our features and user experience.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-3">4. How We Share Your Information</h2>
            <p>We do not sell your personal information. We may share your information only in the following circumstances:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>With Your Consent:</strong> When you authorize publishing to third-party platforms (Facebook, Instagram, LinkedIn, WordPress), your content is shared with those platforms per your instructions.</li>
              <li><strong>Service Providers:</strong> With trusted third-party services (e.g., AI providers, cloud hosting) that help us operate the Service, bound by confidentiality agreements.</li>
              <li><strong>Legal Requirements:</strong> When required by law, legal process, or to protect our rights, safety, or property.</li>
              <li><strong>Team Members:</strong> Within your organization, content and lead data may be visible to other authorized team members as configured.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-3">5. Data Retention</h2>
            <p>
              We retain your information for as long as your account is active or as needed to provide the Service. 
              Security audit logs are automatically deleted after 90 days. 
              You may request deletion of your data at any time (see Section 7).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-3">6. Data Security</h2>
            <p>We implement industry-standard security measures to protect your data, including:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Encrypted password storage (bcrypt hashing)</li>
              <li>Secure HTTP headers (HSTS, CSP, X-Frame-Options)</li>
              <li>Rate limiting and brute-force protection</li>
              <li>Session management with inactivity timeouts</li>
              <li>Security audit logging and monitoring</li>
              <li>OAuth 2.0 for third-party platform connections</li>
            </ul>
            <p>
              While we strive to protect your data, no method of electronic transmission or storage is 100% secure. 
              We cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-3">7. Your Rights & Data Deletion</h2>
            <p>You have the following rights regarding your personal data:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Access:</strong> Request a copy of the data we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data.</li>
              <li><strong>Disconnect:</strong> Revoke access to connected third-party platforms at any time via the Connections page.</li>
              <li><strong>Export:</strong> Request an export of your data in a portable format.</li>
            </ul>
            <p className="mt-3">
              <strong>To request data deletion or exercise any of these rights, contact us at:</strong>
            </p>
            <p className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-2">
              Email: <a href="mailto:privacy@shardalms.com" className="text-blue-600 underline">privacy@shardalms.com</a><br />
              Or use the account settings within the Service to manage your data.
            </p>
            <p className="mt-2">We will respond to your request within 30 days.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-3">8. Third-Party Services</h2>
            <p>
              Our Service integrates with third-party platforms. When you connect these services, their own privacy policies apply to data processed by them:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Meta (Facebook/Instagram): <a href="https://www.facebook.com/privacy/policy" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">facebook.com/privacy/policy</a></li>
              <li>LinkedIn: <a href="https://www.linkedin.com/legal/privacy-policy" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">linkedin.com/legal/privacy-policy</a></li>
              <li>WordPress: <a href="https://automattic.com/privacy/" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">automattic.com/privacy</a></li>
              <li>Canva: <a href="https://www.canva.com/policies/privacy-policy/" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">canva.com/policies/privacy-policy</a></li>
              <li>OpenAI: <a href="https://openai.com/privacy" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">openai.com/privacy</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-3">9. Cookies</h2>
            <p>
              We use session cookies to maintain your login state and preferences. These cookies are essential for the operation of the Service 
              and expire when your session ends or after the configured timeout period.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-3">10. Children's Privacy</h2>
            <p>
              Our Service is not intended for use by anyone under the age of 18. We do not knowingly collect personal information from children. 
              If we become aware that we have collected data from a child, we will take steps to delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page 
              and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-3">12. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy, please contact us:</p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-2">
              <p><strong>Sharda LMS</strong></p>
              <p>Email: <a href="mailto:privacy@shardalms.com" className="text-blue-600 underline">privacy@shardalms.com</a></p>
              <p>Website: <a href="https://shardalms.com" className="text-blue-600 underline">https://shardalms.com</a></p>
            </div>
          </section>

        </div>

        <div className="mt-12 pt-6 border-t border-slate-200 text-center text-xs text-slate-400">
          Sharda LMS &copy; {new Date().getFullYear()}. All rights reserved.
        </div>
      </div>
    </div>
  );
}
